import { format } from 'date-fns';
import { CLUBE_METHOD } from './consts';

const PAID_METHODS = [301, CLUBE_METHOD, 572, 2548];

const safe = (value, fallback = '') => value ?? fallback;

const formatPrice = (price) => {
  const numPrice = parseFloat(price);
  if (isNaN(numPrice)) {
    return '';
  }
  return numPrice.toLocaleString('pt-br', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const sanitizeName = (name) => safe(name).replace(/#/g, '%23');

function isPaidMethod(paymentMethod) {
  return PAID_METHODS.includes(paymentMethod?.id);
}

export function getPaymentStatus(item) {
  if (item?.ifood_paid) {
    return 'Pagamento já efetuado.';
  }

  const paymentMethod = item?.intended_payment_method || item?.payment_method;

  if (isPaidMethod(paymentMethod)) {
    return 'Pagamento já efetuado';
  }

  if (item?.with_withdrawal) {
    return 'Pagamento na retirada.';
  }

  return 'Pagamento na entrega.';
}

const formatAddress = (item, user) => {
  if (item?.with_withdrawal) {
    const addr = user?.adress || {};
    return [
      safe(addr.neighborhood),
      safe(addr.street),
      safe(addr.number),
      safe(addr.city),
      safe(addr.state),
    ]
      .filter(Boolean)
      .join(' - ');
  }

  const deliveryAddress = item?.buyer?.delivery_address || {};
  const baseParts = [
    safe(deliveryAddress.neighborhood),
    safe(deliveryAddress.street),
    safe(deliveryAddress.number),
    safe(deliveryAddress.city),
    safe(deliveryAddress.state),
    safe(deliveryAddress.zip_code),
  ]
    .filter(Boolean)
    .join(' - ');

  const extraParts = [
    safe(deliveryAddress.complement),
    safe(deliveryAddress.reference),
  ]
    .filter(Boolean)
    .map((part) => `\n${part}`);

  return baseParts + extraParts.join('');
};

const formatProducts = (orders) => {
  if (!orders?.length) return '';

  return orders
    .map((prod) => {
      const complementsText =
        prod.complement_categories?.length > 0
          ? prod.complement_categories
              .map((complement_cat) => {
                const categoryName = safe(
                  complement_cat.complement_category?.name
                );
                const complements =
                  complement_cat.order_complements
                    ?.map(
                      (comp) =>
                        `    ${safe(comp.amount)}x - ${sanitizeName(
                          comp.complement?.name
                        )} - R$${formatPrice(comp.complement?.price)}\n`
                    )
                    .join('') || '';

                return `    - ${categoryName}\n${complements}`;
              })
              .join('')
          : '';

      return `\n*${safe(prod.amount)}x - ${sanitizeName(
        prod.product?.name
      )} - R$ ${formatPrice(prod.total_price)}*\n${complementsText}`;
    })
    .join('');
};

/**
 * O total do pedido é a soma do preço do pedido, do desconto e da taxa de entrega.
 *
 */
const calculateTotal = (item) => {
  const price = Number(item?.total_price) || 0;
  const discount = Number(item?.discount_total) || 0;
  const deliveryTax = Number(item?.delivery_tax_price) || 0;
  return price + discount + deliveryTax;
};

const formatPriceBreakdown = (item, total) => {
  /**
   * Os cupons do ifood podem entrar como:
   * 1. ifood_discount: Quando o desconto é dado pelo ifood
   * 2. x: Quando o desconto é dado pelo próprio restaurante, no ifood
   *
   * Outros descontos possíveis:
   * 1. Cashback(rescue.cashback): Quando o usuário resgata cashback do clube
   * 2. Cupom Takeat (coupon_discount): Quando o usuário resgata um cupom Takeat
   * O coupon_discoint já foi calculado na função `calculateTotal`, e então não é
   * somado novamente aqui
   */
  const hasDiscount =
    item?.rescue || item?.coupon_discount || item?.ifood_discount;
  const rescueAmount = item?.rescue?.cashback || 0;
  const couponDiscount = item?.coupon_discount || 0;
  const ifoodDiscount = item?.ifood_discount || 0;
  const finalTotal =
    item.total_delivery_price - (ifoodDiscount || 0) - rescueAmount;

  let breakdown = '';

  if (hasDiscount) {
    breakdown += `Subtotal com taxa (se houver): *R$ ${formatPrice(total)}*\n`;
  }

  if (item?.rescue) {
    breakdown += `Resgate Clube: *R$${formatPrice(rescueAmount)}*\n`;
  }

  if (item?.coupon_discount) {
    breakdown += `Desconto cupom: *R$${formatPrice(couponDiscount)}*\n`;
  }

  if (item?.ifood_discount && Number(item?.ifood_discount) > 0) {
    breakdown += `Desconto iFood: *R$${formatPrice(ifoodDiscount)}*\n`;
  }

  breakdown += `Total com taxa (se houver): *R$ ${formatPrice(finalTotal)}*`;

  return breakdown;
};

const formatMotoboyMessage = (item, orders) => {
  const userAddress = formatAddress(item, null);
  const buyerName = safe(item?.buyer?.name);
  const buyerPhone = safe(item?.buyer?.phone);
  const localizer = item?.buyer?.localizer
    ? `\nID iFood: ${item.buyer.localizer}`
    : '';
  const paymentStatus = getPaymentStatus(item);
  const isPaid = paymentStatus === 'Pagamento já efetuado';

  let paymentMethod = 'Não informado';
  if (item?.intended_payment_method) {
    paymentMethod = safe(item?.intended_payment_method?.name);
  } else if (item?.payment_method) {
    paymentMethod = safe(item?.payment_method?.name);
  }

  const userChange = parseFloat(item?.user_change) || 0;
  const productsText = formatProducts(orders);
  const total = calculateTotal(item);

  const deliveryTime = item?.ifood_delivery_time
    ? `\n\nHorário previsto: ${format(
        new Date(item.ifood_delivery_time),
        'HH:mm'
      )}`
    : '';

  let paymentInfo = '';
  if (!isPaid) {
    paymentInfo = `\nMétodo: ${paymentMethod}\n${formatPriceBreakdown(
      item,
      total
    )}`;
  }

  const changeInfo =
    userChange > 0 ? `\nTroco para: R$ ${formatPrice(userChange)}` : '';

  return `Pedido: #${safe(
    item?.attendance_password
  )}${deliveryTime}\n\nEndereço: ${userAddress}\n\nCliente: ${buyerName} - ${buyerPhone}${localizer}\n\n${paymentStatus}${paymentInfo}${changeInfo}\n\nDados do pedido:${productsText}`;
};

const getDeliveryText = (status, orders, item, user, ifood_on_demand_id) => {
  try {
    switch (status) {
      case 'motoboy':
        return formatMotoboyMessage(item, orders);
      default:
        return '';
    }
  } catch (err) {
    console.log('Error generating delivery text:', err);
    return '';
  }
};

export { getDeliveryText };
